import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="turnos-form-validation"
export default class extends Controller {
  static targets = [
    "targetInput",
    "targetHidden",
    "targetError",
    "patientInput",
    "patientHidden",
    "patientError",
    "submit",
    "internacionWarning"
  ]

  static values = {
    readonly: Boolean,
    tipoAtencion: String,
    estadoInternacionUrlBase: String
  }

  connect() {
    this.onPatientCreated = this.onPatientCreated.bind(this)
    window.addEventListener("turnos:patient-created", this.onPatientCreated)
    this.validate()
  }

  disconnect() {
    window.removeEventListener("turnos:patient-created", this.onPatientCreated)
  }

  onTargetInput() {
    this.clearHiddenIfTyped(this.targetInputTarget, this.targetHiddenTarget)
    this.validate()
  }

  onPatientInput() {
    this.clearHiddenIfTyped(this.patientInputTarget, this.patientHiddenTarget)
    this.hideInternacionWarning()
    this.validate()
  }

  onTargetSelected() {
    this.validate()
  }

  onPatientSelected() {
    const hidden = (this.patientHiddenTarget.value || "").trim()
    const integralMatch = hidden.match(/^integral:(\d+):(\d*)$/)

    if (integralMatch) {
      this.patientHiddenTarget.value = ""
      this.hideInternacionWarning()
      this.validate()
      this.vincularIntegral(integralMatch[1], integralMatch[2])
      return
    }

    this.validate()

    if (/^\d+$/.test(hidden)) {
      this.fetchEstadoInternacion(hidden)
    } else {
      this.hideInternacionWarning()
    }
  }

  vincularIntegral(dni, tipoDocumentoIdentidad) {
    if (this.hasPatientInputTarget) this.patientInputTarget.disabled = true

    const body = new URLSearchParams({ dni: dni, tipo_documento_identidad: tipoDocumentoIdentidad })
    const csrfMeta = document.querySelector("meta[name='csrf-token']")

    fetch("/pacientes_consultorio/vincular_integral", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        ...(csrfMeta ? { "X-CSRF-Token": csrfMeta.content } : {})
      },
      body: body
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (this.hasPatientInputTarget) this.patientInputTarget.disabled = false

        if (ok) {
          this.onPatientCreated({ detail: data })
        } else if (data.fallback_url && window.Turbo) {
          window.Turbo.visit(data.fallback_url, { frame: "paciente_consultorio_remote_modal" })
        }
      })
      .catch(() => {
        if (this.hasPatientInputTarget) this.patientInputTarget.disabled = false
      })
  }

  onPatientCreated(event) {
    const id = (event?.detail?.id || "").toString().trim()
    const label = (event?.detail?.label || "").toString().trim()
    if (!id || !label) return

    this.patientInputTarget.value = label
    this.patientHiddenTarget.value = id
    // Solo "input" (dispara validate()): "change" está atado a onPatientSelected,
    // y ya estamos dentro de ese flujo (vincularIntegral) o de la creación manual;
    // redispararlo re-entraría y duplicaría el chequeo de estado_internacion.
    this.patientHiddenTarget.dispatchEvent(new Event("input"))
    this.validate()

    const internacion = event?.detail?.internacion
    if (internacion && (internacion.tipo === "internado" || internacion.tipo === "guardia")) {
      this.showInternacionWarning(internacion)
    } else {
      this.hideInternacionWarning()
    }
  }

  fetchEstadoInternacion(id) {
    if (!this.hasEstadoInternacionUrlBaseValue) return

    fetch(`${this.estadoInternacionUrlBaseValue}/${id}/estado_internacion`, {
      headers: { Accept: "application/json" }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) return

        if (data.internado || data.en_guardia) {
          this.showInternacionWarning({
            tipo: data.en_guardia ? "guardia" : "internado",
            sector: data.sector,
            habitacion: data.habitacion
          })
        } else {
          this.hideInternacionWarning()
        }
      })
      .catch(() => this.hideInternacionWarning())
  }

  showInternacionWarning(detalle) {
    if (!this.hasInternacionWarningTarget) return

    const esGuardia = detalle.tipo === "guardia"
    const titulo = esGuardia ? "Paciente en guardia" : "Paciente internado"
    const ubicacion = [detalle.sector ? `Sector ${detalle.sector}` : null, detalle.habitacion ? `Hab. ${detalle.habitacion}` : null]
      .filter(Boolean)
      .join(" · ")

    const target = this.internacionWarningTarget
    target.textContent = ""

    const strong = document.createElement("strong")
    strong.textContent = titulo
    target.appendChild(strong)

    if (ubicacion) {
      target.appendChild(document.createTextNode(` — ${ubicacion}`))
    }

    const detalleDiv = document.createElement("div")
    detalleDiv.className = "small"
    detalleDiv.textContent = ""
    target.appendChild(detalleDiv)

    target.classList.remove("d-none")
  }

  hideInternacionWarning() {
    if (!this.hasInternacionWarningTarget) return

    this.internacionWarningTarget.classList.add("d-none")
    this.internacionWarningTarget.innerHTML = ""
  }

  onTipoChanged(event) {
    const tipo = event?.detail?.tipo
    if (tipo) {
      this.tipoAtencionValue = tipo
    }

    this.validate()
  }

  validate() {
    if (!this.hasSubmitTarget) return

    if (this.readonlyValue) {
      this.submitTarget.disabled = true
      return
    }

    const targetOk = this.validTargetSelected()
    const patientOk = this.validPatientSelected()

    this.renderFieldState(
      this.targetInputTarget,
      this.targetErrorTarget,
      targetOk,
      this.targetRequiredMessage()
    )

    this.renderFieldState(
      this.patientInputTarget,
      this.patientErrorTarget,
      patientOk,
      "Debes seleccionar un paciente desde la lista."
    )

    this.submitTarget.disabled = !(targetOk && patientOk)
  }

  validTargetSelected() {
    const visible = (this.targetInputTarget.value || "").trim()
    const hidden = (this.targetHiddenTarget.value || "").trim()

    if (visible.length === 0) return false
    if (hidden.length === 0) return false

    if (this.tipoAtencionValue === "imagenes") {
      return /^srv:\d+$/.test(hidden)
    }

    if (this.tipoAtencionValue === "ambulatorio" || this.tipoAtencionValue === "art") {
      return /^prof:[^:]+$/.test(hidden)
    }

    return /^(prof:|srv:|\d+$)/.test(hidden)
  }

  targetRequiredMessage() {
    if (this.tipoAtencionValue === "imagenes") {
      return "Debes seleccionar un servicio desde la lista."
    }

    return "Debes seleccionar un profesional desde la lista."
  }

  validPatientSelected() {
    const visible = (this.patientInputTarget.value || "").trim()
    const hidden = (this.patientHiddenTarget.value || "").trim()

    if (visible.length === 0) return false
    if (hidden.length === 0) return false

    return /^\d+$/.test(hidden)
  }

  clearHiddenIfTyped(visibleInput, hiddenInput) {
    const visible = (visibleInput.value || "").trim()
    const hidden = (hiddenInput.value || "").trim()

    if (!visible || !hidden) {
      hiddenInput.value = ""
      return
    }

    if (!this.hasFocusWithinField(visibleInput)) return

    hiddenInput.value = ""
  }

  hasFocusWithinField(field) {
    return document.activeElement === field
  }

  renderFieldState(input, errorEl, valid, message) {
    const showError = !valid

    input.classList.toggle("is-invalid", showError)
    input.setAttribute("aria-invalid", showError ? "true" : "false")

    if (showError) {
      errorEl.textContent = message
      errorEl.classList.remove("d-none")
    } else {
      errorEl.textContent = ""
      errorEl.classList.add("d-none")
    }
  }
}
